import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Download,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import axios from 'axios';

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface Segment {
  segmentid: number;
  segmentname: string;
}

interface DomainBurst {
  domain: string;
  count: number;
  windowStart: number;
  windowEnd: number;
  firstUnsub: string;
  lastUnsub: string;
}

interface ContactRow {
  email: string;
  unsubscribedate: string;
  listid: number;
  subscriberid: number;
}

interface Props {
  lists: List[];
  segments: Segment[];
}

type Scope = 'segment' | 'list';

function listLabel(l: List): string {
  return l.name ?? l.listname ?? `List ${l.listid}`;
}

export default function MassUnsubReview({ lists, segments }: Props) {
  const [scope, setScope] = useState<Scope>('segment');
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [segmentSearch, setSegmentSearch] = useState('');
  const [listId, setListId] = useState<number | ''>('');
  const [domain, setDomain] = useState('');
  const [lookbackMonths, setLookbackMonths] = useState(36);
  const [windowDays, setWindowDays] = useState(7);
  const [minCount, setMinCount] = useState(10);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scopeLabel, setScopeLabel] = useState<string | null>(null);
  const [domains, setDomains] = useState<DomainBurst[]>([]);

  const [selected, setSelected] = useState<DomainBurst | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const filteredSegments = useMemo(() => {
    const q = segmentSearch.trim().toLowerCase();
    if (!q) return segments;
    return segments.filter(
      (s) =>
        s.segmentname.toLowerCase().includes(q) ||
        String(s.segmentid).includes(q)
    );
  }, [segments, segmentSearch]);

  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => listLabel(a).localeCompare(listLabel(b))),
    [lists]
  );

  const canRun =
    scope === 'segment'
      ? segmentId !== ''
      : listId !== '' && domain.trim().length > 0;

  const resetResults = () => {
    setDomains([]);
    setSelected(null);
    setContacts([]);
    setScopeLabel(null);
    setError(null);
  };

  const scopeQueryParams = (burst: DomainBurst) => {
    const params = new URLSearchParams({
      scope,
      domain: burst.domain,
      windowStart: String(burst.windowStart),
      windowEnd: String(burst.windowEnd),
    });
    if (scope === 'segment' && segmentId !== '') {
      params.set('segmentId', String(segmentId));
    }
    if (scope === 'list' && listId !== '') {
      params.set('listId', String(listId));
    }
    return params;
  };

  const handleRun = async () => {
    if (!canRun) {
      setError(
        scope === 'segment'
          ? 'Select a segment first.'
          : 'Select a list and enter a domain.'
      );
      return;
    }
    setLoading(true);
    resetResults();
    try {
      const body =
        scope === 'segment'
          ? { scope, segmentId, lookbackMonths, windowDays, minCount }
          : {
              scope,
              listId,
              domain: domain.trim().replace(/^@/, ''),
              lookbackMonths,
              windowDays,
              minCount,
            };
      const res = await axios.post<{
        scope: Scope;
        segmentName?: string;
        listName?: string;
        domains: DomainBurst[];
      }>('/api/interspire/utilities/mass-unsub-review', body, { timeout: 180_000 });
      setScopeLabel(
        res.data.scope === 'list'
          ? res.data.listName ?? `List ${listId}`
          : res.data.segmentName ?? `Segment ${segmentId}`
      );
      setDomains(res.data.domains);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Review failed');
    } finally {
      setLoading(false);
    }
  };

  const openDomain = async (row: DomainBurst) => {
    if (!canRun) return;
    setSelected(row);
    setContacts([]);
    setContactsError(null);
    setContactsLoading(true);
    try {
      const res = await axios.get<{ rows: ContactRow[] }>(
        `/api/interspire/utilities/mass-unsub-review/contacts?${scopeQueryParams(row)}`,
        { timeout: 120_000 }
      );
      setContacts(res.data.rows);
    } catch (e: any) {
      setContactsError(e?.response?.data?.error ?? e?.message ?? 'Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const exportContacts = async () => {
    if (!selected || !canRun) return;
    setExporting(true);
    try {
      const res = await axios.get(
        `/api/interspire/utilities/mass-unsub-review/contacts/export?${scopeQueryParams(selected)}`,
        { responseType: 'blob', timeout: 0 }
      );
      const scopeTag =
        scope === 'list' ? `list_${listId}` : `seg_${segmentId}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mass_unsub_${selected.domain.replace(/[^a-z0-9._-]/gi, '_')}_${scopeTag}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Users className="h-4 w-4 text-amber-500" />
          Mass Unsub Review
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Find domains where many contacts unsubscribed inside a short window (historical). Uses
          unsubscribe-event times when present, otherwise the list&apos;s unsubscribe timestamp.
          List + Domain also includes subdomains (e.g. <code className="text-xs">state.mn.us</code> matches{' '}
          <code className="text-xs">courts.state.mn.us</code>).
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            { id: 'segment' as const, label: 'Segment' },
            { id: 'list' as const, label: 'List + Domain' },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              setScope(opt.id);
              resetResults();
            }}
            className={`px-3 py-1.5 text-sm rounded-md border ${
              scope === opt.id
                ? 'bg-amber-600 text-white border-amber-600'
                : 'border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scope === 'segment' ? (
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Segment <span className="text-red-500">*</span>
            </label>
            <input
              type="search"
              value={segmentSearch}
              onChange={(e) => setSegmentSearch(e.target.value)}
              placeholder="Filter segments…"
              className="mb-1.5 w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1.5"
            />
            <select
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
            >
              <option value="">Select a segment…</option>
              {filteredSegments.map((s) => (
                <option key={s.segmentid} value={s.segmentid}>
                  {s.segmentname} (ID {s.segmentid})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
                List <span className="text-red-500">*</span>
              </label>
              <select
                value={listId}
                onChange={(e) => setListId(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
              >
                <option value="">Select a list…</option>
                {sortedLists.map((l) => (
                  <option key={l.listid} value={l.listid}>
                    {listLabel(l)} (ID {l.listid})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
                Domain <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canRun && !loading) handleRun();
                }}
                placeholder="agency.gov"
                className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
              />
            </div>
          </>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Look back (months)
          </label>
          <input
            type="number"
            min={6}
            max={60}
            value={lookbackMonths}
            onChange={(e) =>
              setLookbackMonths(Math.min(60, Math.max(6, Number(e.target.value) || 36)))
            }
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          />
          <p className="text-xs text-gray-400 mt-1">Default 36 (3 years). Range 6–60.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Burst window (days)
          </label>
          <input
            type="number"
            min={1}
            max={90}
            value={windowDays}
            onChange={(e) =>
              setWindowDays(Math.min(90, Math.max(1, Number(e.target.value) || 7)))
            }
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          />
          <p className="text-xs text-gray-400 mt-1">Densest span length to search (default 7).</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Min unsubs in window
          </label>
          <input
            type="number"
            min={2}
            max={10000}
            value={minCount}
            onChange={(e) =>
              setMinCount(Math.min(10000, Math.max(2, Number(e.target.value) || 10)))
            }
            className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleRun}
        disabled={loading || !canRun}
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
      >
        {loading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4" />
        )}
        {loading ? 'Reviewing…' : 'Run Review'}
      </button>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {scopeLabel && !loading && (
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Results for <span className="font-semibold">{scopeLabel}</span>
          {scope === 'list' && domain.trim() ? (
            <>
              {' · '}
              <span className="font-mono">@{domain.trim().replace(/^@/, '')}</span>
            </>
          ) : null}
          {' · '}
          {domains.length.toLocaleString()} domain{domains.length !== 1 ? 's' : ''} with ≥{minCount}{' '}
          unsubs in ≤{windowDays} day{windowDays !== 1 ? 's' : ''}
        </p>
      )}

      {domains.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-slate-700 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Domain</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Unsubs</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Window start</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Window end</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {domains.map((d) => (
                <tr
                  key={`${d.domain}-${d.windowStart}`}
                  className={`hover:bg-gray-50 dark:hover:bg-slate-700/40 ${
                    selected?.domain === d.domain && selected.windowStart === d.windowStart
                      ? 'bg-amber-50 dark:bg-amber-900/20'
                      : ''
                  }`}
                >
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-slate-100">@{d.domain}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-400">
                    {d.count.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{d.firstUnsub || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{d.lastUnsub || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openDomain(d)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && scopeLabel && domains.length === 0 && !error && (
        <p className="text-sm text-gray-400 text-center py-6">
          No domains met the threshold for this {scope === 'list' ? 'list/domain' : 'segment'} and
          lookback.
        </p>
      )}

      {selected && (
        <div className="border border-amber-200 dark:border-amber-800/50 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/40">
            <div className="text-sm text-amber-900 dark:text-amber-200">
              <span className="font-semibold">@{selected.domain}</span>
              {' · '}
              {selected.count.toLocaleString()} unsubs
              {' · '}
              {selected.firstUnsub} → {selected.lastUnsub}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportContacts}
                disabled={exporting || contactsLoading || contacts.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {exporting ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setContacts([]);
                }}
                className="p-1.5 text-gray-500 hover:text-gray-800 dark:hover:text-slate-200"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {contactsError && (
            <div className="px-4 py-2 text-sm text-red-600">{contactsError}</div>
          )}

          {contactsLoading ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">
              <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading contacts…
            </div>
          ) : (
            <div className="overflow-x-auto max-h-80">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-slate-700/60">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Unsubscribed</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">List</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {contacts.map((c) => (
                    <tr key={`${c.subscriberid}-${c.unsubscribedate}`}>
                      <td className="px-3 py-1.5 text-gray-900 dark:text-slate-100">{c.email}</td>
                      <td className="px-3 py-1.5 text-amber-700 dark:text-amber-400">
                        {c.unsubscribedate || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right text-gray-500">#{c.listid}</td>
                    </tr>
                  ))}
                  {!contacts.length && !contactsError && (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-gray-400">
                        No contacts in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
